import type { IconType } from 'src/components/Shared/Icon/types';
import {
  LISTING_CONTENT_TYPES,
  LISTING_STATE_KEYS,
} from 'src/components/VendorPortal/ListingLayout/shared/constants';
import type { ListingTableMap } from 'src/components/VendorPortal/ListingLayout/shared/types';
import {
  SCHEDULE_SLOT_DEFINITIONS,
  SCHEDULE_SLOT_KEYS,
  type ScheduleSlotKeyType,
} from 'src/helper/schedule-types';
import type { FilterMap, OptionType, SortMap, TypedObj } from 'src/helper/shared-types';
import type { WorkOrderFilters } from 'src/redux/vendor-portal/work-order/types';
import * as yup from 'yup';

interface WorkOrderTabsType<T> extends TypedObj<T> {
  open: T;
  paused: T;
  closed: T;
}

export const WO_STATUS_KEYS = {
  CANCELED: 'canceled',
  COMPLETED: 'completed',
  READY_TO_INVOICE: 'ready_to_invoice',
  CLOSED: 'closed',
  EN_ROUTE: 'en_route',
  PAUSED: 'paused',
  READY_TO_SCHEDULE: 'ready_to_schedule',
  SCHEDULED: 'scheduled',
  WORK_IN_PAUSED: 'work_in_paused',
  IN_PROGRESS: 'in_progress',
  IN_PROGRESS_PAUSED: 'in_progress_paused',
  WORK_IN_PROGRESS: 'work_in_progress',
  WORKING: 'working',
  EN_ROUTE_PAUSED: 'en_route_paused',
  PAUSE_EN_ROUTE: 'pause_en_route',
  PAUSE_TIMER: 'pause-timer',
  QUALITY_CHECK: 'quality_check',
  SCHEDULE_RETURN_TRIP: 'schedule_return_trip',
  MISSING_INFO: 'missing_info',
  ALL_COMPLETED: 'all_completed',
} as const;

// Work-order statuses in which the vendor's Service Report and the Unresolve panel are
// READ-ONLY: the coordinator's quality-check review, completion, the post-completion
// invoicing state (`ready_to_invoice` renders as the "Invoicing" tag — `invoice_status` is
// a separate overlay on the same completed WO), and `canceled`. A plain done issue on any of
// these must not expose edit/delete affordances (ORI-592).
//   • `closed` is included DEFENSIVELY: the BE does not currently emit `closed` as a vendor
//     WO status (it's a tab bucket, and post-close WOs surface as quality_check /
//     ready_to_invoice), but it is kept for parity with the terminal list in
//     `useTripSummary` (COST_BREAKDOWN_STATUSES) and to future-proof a real closed state.
// Shared by ServiceReportForm and UnresolveDetails so those two read-only panels can't drift.
// (Note: `useTripSummary.COST_BREAKDOWN_STATUSES` is a SEPARATE terminal-status list for a
// different purpose — trip-cost rendering — so this is the source of truth for read-only
// gating only, not for every terminal-status decision in the app.)
export const WO_READ_ONLY_STATUS_VALUES: ReadonlyArray<string> = [
  WO_STATUS_KEYS.QUALITY_CHECK,
  WO_STATUS_KEYS.COMPLETED,
  WO_STATUS_KEYS.READY_TO_INVOICE,
  WO_STATUS_KEYS.CLOSED,
  WO_STATUS_KEYS.CANCELED,
];

export const WO_STATUS_LABELS = {
  EN_ROUTE: 'En-Route',
  EN_ROUTE_PAUSED: 'En-Route Paused',
  IN_PROGRESS: 'Work In Progress',
  IN_PROGRESS_PAUSED: 'Work In Progress',
  SCHEDULED: 'Scheduled',
  COMPLETED: 'Completed',
  PAUSED: 'Paused',
  CANCELED: 'Canceled',
} as const;

export const WO_DATA_KEYS = {
  ADDRESS: 'address',
  CATEGORY: 'category',
  COMPLETED: 'completed_at',
  PAUSED: 'paused_at',
  SCHEDULE: 'scheduled_date',
  STATUS: 'status',
  WO_ID: 'work_order_id',
  WO_NUMBER: 'work_order_number',
  INVOICE: 'invoice',
} as const;

export const WO_FILTER_KEYS = {
  CATEGORY: WO_DATA_KEYS.CATEGORY,
  COMPLETED: 'closed_date',
  PAUSED: 'paused_date',
  SCHEDULE: WO_DATA_KEYS.SCHEDULE,
  STATUS: WO_DATA_KEYS.STATUS,
} as const;

export const WO_SORT_KEYS = {
  CLOSED: 'closed',
  PAUSED: 'paused',
  SCHEDULE: 'scheduled',
} as const;

export const WO_DATA_LABELS = {
  ADDRESS: 'Address',
  CATEGORY: 'Service Category',
  CLOSED: 'Closed Date',
  COMPLETED: 'Completion Date',
  PAUSED: 'Paused Date',
  SCHEDULE: 'Schedule',
  STATUS: 'Status',
  WO_NUMBER: 'ID',
  INVOICE: 'Invoice',
  ACTIONS: 'Actions',
} as const;

export const WO_DEFAULT_FILTERS: WorkOrderFilters = {
  status: [],
  category: [],
  closed_date: [],
  paused_date: [],
  scheduled_date: [],
};

export const WO_FILTERS_MAPPING: FilterMap[] = [
  {
    isMultiSelect: true,
    key: WO_FILTER_KEYS.STATUS,
    label: WO_DATA_LABELS.STATUS,
    options: [],
  },
  {
    isMultiSelect: true,
    key: WO_FILTER_KEYS.CATEGORY,
    label: WO_DATA_LABELS.CATEGORY,
    options: [],
  },
];

export const WO_DATE_FILTER_OPTIONS: OptionType[] = [
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'this_week' },
  { label: 'This Month', value: 'this_month' },
];

export const WO_DATE_FILTERS: WorkOrderTabsType<FilterMap> = {
  open: {
    key: WO_FILTER_KEYS.SCHEDULE,
    label: WO_DATA_LABELS.SCHEDULE,
    options: WO_DATE_FILTER_OPTIONS,
  },
  paused: {
    key: WO_FILTER_KEYS.PAUSED,
    label: WO_DATA_LABELS.PAUSED,
    options: WO_DATE_FILTER_OPTIONS,
  },
  closed: {
    key: WO_FILTER_KEYS.COMPLETED,
    label: WO_DATA_LABELS.CLOSED,
    options: WO_DATE_FILTER_OPTIONS,
  },
};

export const WO_DATE_SORT_LABELS: {
  ASCENDING: string;
  DESCENDING: string;
} = {
  ASCENDING: 'Oldest First',
  DESCENDING: 'Newest First',
};

export const WO_PAGE_TITLE: string = 'Work Orders';

// Row identity uses work_order_id, not work_order_number: the WO number is nullable
// by design for some sources (e.g. RentersWarehouse), which produced duplicate
// `tr-null` React keys / DOM ids. work_order_id is always present on every row.
export const WO_PRIMARY_KEY: string = WO_DATA_KEYS.WO_ID;

export const WO_SCHEDULE_SORT: SortMap = {
  key: WO_SORT_KEYS.SCHEDULE,
  ascendingLabel: 'Soonest First',
  descendingLabel: 'Latest First',
  label: WO_DATA_LABELS.SCHEDULE,
};

export const WO_SORT_MAPPING: WorkOrderTabsType<SortMap[]> = {
  open: [WO_SCHEDULE_SORT],
  paused: [
    WO_SCHEDULE_SORT,
    {
      key: WO_SORT_KEYS.PAUSED,
      ascendingLabel: WO_DATE_SORT_LABELS.ASCENDING,
      descendingLabel: WO_DATE_SORT_LABELS.DESCENDING,
      label: WO_DATA_LABELS.PAUSED,
    },
  ],
  closed: [
    WO_SCHEDULE_SORT,
    {
      key: WO_SORT_KEYS.CLOSED,
      ascendingLabel: WO_DATE_SORT_LABELS.ASCENDING,
      descendingLabel: WO_DATE_SORT_LABELS.DESCENDING,
      label: WO_DATA_LABELS.CLOSED,
    },
  ],
};

export const WO_SORT_DEFAULT: WorkOrderTabsType<string> = {
  open: WO_SORT_KEYS.SCHEDULE,
  paused: WO_SORT_KEYS.PAUSED,
  closed: WO_SORT_KEYS.CLOSED,
};

export const WO_STATE_KEY = 'vendor.work-orders';

export const WO_STATE_KEYS = {
  ...LISTING_STATE_KEYS,
  CATEGORY_OPTIONS: 'categoryOptions',
} as const;

export const WO_TABS_KEYS = {
  OPEN: 'open',
  PAUSED: 'paused',
  CLOSED: 'closed',
} as const;

export const WO_DETAILS_TABS_KEYS = {
  DETAILS: 'details',
  TRIPS: 'trips',
  QUOTES: 'quotes',
} as const;

export const WO_TAB_FILTERS: WorkOrderTabsType<string[]> = {
  open: [`all_${WO_TABS_KEYS.OPEN}`],
  closed: [`all_${WO_TABS_KEYS.CLOSED}`],
  paused: [`all_${WO_TABS_KEYS.PAUSED}`],
};

export const WO_TABLE_ID: string = 'workOrdersTable';

export const WO_TABLE_MAPPING_COMMON: ListingTableMap[] = [
  {
    key: WO_DATA_KEYS.WO_NUMBER,
    label: WO_DATA_LABELS.WO_NUMBER,
  },
  {
    key: WO_DATA_KEYS.CATEGORY,
    label: WO_DATA_LABELS.CATEGORY,
    type: LISTING_CONTENT_TYPES.CATEGORY,
  },
  {
    key: WO_DATA_KEYS.ADDRESS,
    label: WO_DATA_LABELS.ADDRESS,
    type: LISTING_CONTENT_TYPES.ADDRESS,
  },
  {
    key: WO_DATA_KEYS.SCHEDULE,
    label: WO_DATA_LABELS.SCHEDULE,
    sortable: true,
    sortKey: WO_SORT_KEYS.SCHEDULE,
    type: LISTING_CONTENT_TYPES.CUSTOM,
  },
];

export const WO_TABLE_MAPPING_CLOSED_DATE: ListingTableMap = {
  key: WO_DATA_KEYS.COMPLETED,
  label: WO_DATA_LABELS.COMPLETED,
  sortable: true,
  sortKey: WO_SORT_KEYS.CLOSED,
  type: LISTING_CONTENT_TYPES.DATE_TIME,
};

export const WO_TABLE_MAPPING_PAUSED_DATE: ListingTableMap = {
  key: WO_DATA_KEYS.PAUSED,
  label: WO_DATA_LABELS.PAUSED,
  sortable: true,
  sortKey: WO_SORT_KEYS.PAUSED,
  type: LISTING_CONTENT_TYPES.DATE_TIME,
};

export const WO_TABLE_MAPPING_STATUS: ListingTableMap = {
  key: WO_DATA_KEYS.STATUS,
  label: WO_DATA_LABELS.STATUS,
  type: LISTING_CONTENT_TYPES.STATUS,
};

export const WO_TABLE_MAPPING_MESSAGES: ListingTableMap = {
  key: 'messages',
  label: WO_DATA_LABELS.ACTIONS,
  type: LISTING_CONTENT_TYPES.CUSTOM,
};

export const WO_TAB_STATUS_FILTERS: WorkOrderTabsType<OptionType[]> = {
  open: [
    { label: 'Schedule', value: WO_STATUS_KEYS.SCHEDULED },
    { label: 'En-route', value: WO_STATUS_KEYS.EN_ROUTE },
    { label: 'En-route Paused', value: WO_STATUS_KEYS.EN_ROUTE_PAUSED },
    { label: 'Work in Progress', value: WO_STATUS_KEYS.WORKING },
  ],
  paused: [{ label: 'Paused', value: WO_STATUS_KEYS.PAUSED }],
  closed: [
    { label: 'Completed', value: WO_STATUS_KEYS.ALL_COMPLETED },
    { label: 'Canceled', value: WO_STATUS_KEYS.CANCELED },
    { label: 'Quality Check', value: WO_STATUS_KEYS.QUALITY_CHECK },
  ],
};

export const WO_TABS_MAPPING = [
  {
    key: 'open',
    label: 'Open',
  },
  {
    key: 'paused',
    label: 'Paused',
  },
  {
    key: 'closed',
    label: 'Closed',
    hideCount: true,
  },
];

export const WO_SIDE_PANEL_TITLE = {
  DESKTOP: 'Work Order',
  MOBILE: 'WO Details',
};

export const WO_DETAILS_TABS_MAPPING = [
  {
    key: 'details',
    label: 'Details',
  },
  {
    key: 'trips',
    label: 'Trips',
  },
  {
    key: 'quotes',

    label: 'Quotes',
  },
];

export const WORK_ORDER_MODAL_KEYS = {
  HANDLE_MATERIAL: 'materialModal',
  OTHER_ACTION: 'otherActionModal',
  START_ENROUTE: 'startEnrouteModal',
  ALL_ISSUES_CLOSED: 'allIssuesClosedModal',
  RESIDENT_INFO: 'residentInfoModal',
  SELECT_SCHEDULE_MODAL: 'selectScheduleModal',
  QUOTE_REMOVAL_MODAL: 'quoteRemovalModal',
  QUOTE_TASK_REMOVAL_MODAL: 'quoteTaskRemovalModal',
  MARK_AS_PENDING_MODAL: 'markAsPendingModal',
  OPTION_MODAL: 'optionModal',
  ADJUST_ELAPSED_TIME_MODAL: 'adjustElapsedTimeModal',
  DIRECTION: 'directionModal',
  EDIT_QUOTE_REASON: 'editQuoteReasonModal',
} as const;

export type ActionItemType = {
  key: string;
  icon: IconType;
  title: string;
  description: string;
};

export const WO_DETAILS_ACTION_ITEMS: {
  CANCEL_WO: ActionItemType;
  PAUSE_TIMER: ActionItemType;
  RESCHEDULE: ActionItemType;
  PAUSE_EN_ROUTE: ActionItemType;
} = {
  CANCEL_WO: {
    key: 'cancel-workorder',
    icon: 'x-circle',
    title: 'Cancel Trip',
    description: 'Cancel this scheduled visit',
  },
  PAUSE_TIMER: {
    key: 'pause-timer',
    icon: 'pause-circle',
    title: 'Pause Job Timer',
    description: "Stops the job timer — paused time isn't billed until you resume",
  },
  PAUSE_EN_ROUTE: {
    key: 'pause_en_route',
    icon: 'pause-circle',
    title: 'Pause En-route',
    description: "Stops the travel timer — paused travel isn't billed until you resume",
  },
  RESCHEDULE: {
    key: 'reschedule',
    icon: 'clock-rewind',
    title: 'Reschedule',
    description: 'Pause work and return later',
  },
};

export const RESCHEDULE_STEPS = {
  ACCESS_ISSUE_REASONS: 'access-issue-reasons',
  LOCKBOX_GATE_CALL: 'lockbox-gate-call',
  LOCKBOX_GATE_FORM: 'lockbox-gate-form',
  MULTI_DAY_FORM: 'multi-day-form',
  OTHER_ACCESS_CALL: 'other-access-call',
  OTHER_ACCESS_FORM: 'other-access-form',
  OTHER_ISSUE_FORM: 'other-issue-form',
  PARTS_NEEDED_LATER: 'parts-needed-later',
  PARTS_NEEDED_TIME: 'parts-needed-time',
  PARTS_NEEDED_TODAY: 'parts-needed-today',
  RESCHEDULE_SUCCESS: 'reschedule-success',
  NOT_HOME_CALL: 'not-home-call',
  NOT_HOME_FORM: 'not-home-form',
  WEATHER_FORM: 'weather-form',
  WORK_ORDER_PAUSED: 'work-order-paused',
} as const;

export const RESCHEDULE_REASONS = {
  MULTI_DAY_JOB: 'multi_day_job',
  OTHER_ISSUE: 'other_issue',
  PARTS_NEEDED_TODAY: 'parts_needed_today',
  PARTS_NEEDED_ANOTHER_DAY: 'parts_needed_another_day',
  REPORT_ACCESS_ISSUE: 'report_access_issue',
  WEATHER: 'weather',
} as const;

export const RESCHEDULE_TITLES = {
  ACCESS_ISSUE_REASONS: 'Select Access Issue',
  LOCKBOX_GATE_ISSUE: 'Lockbox/Gate Issue',
  MULTI_DAY: 'Multi-Day Work Order',
  OTHER_ACCESS_ISSUE: 'Other Access Issue',
  OTHER_ISSUE: 'Other Issue',
  PARTS_NEEDED: 'Parts Needed',
  RESIDENT_NOT_HOME: 'Resident Not Home',
  WEATHER: 'Weather',
};

export const RESCHEDULE_SUBTITLES: Record<
  Exclude<RescheduleStepsType, 'reschedule-success' | 'work-order-paused'>,
  string
> = {
  [RESCHEDULE_STEPS.ACCESS_ISSUE_REASONS]:
    'Choose the reason you were unable to access the property.',
  [RESCHEDULE_STEPS.LOCKBOX_GATE_CALL]:
    'If the code doesn’t work, call support. If needed, you can proceed with rescheduling.',
  [RESCHEDULE_STEPS.LOCKBOX_GATE_FORM]:
    'Reschedule your visit after resolving the lockbox or access issue.',
  [RESCHEDULE_STEPS.MULTI_DAY_FORM]: 'Tell us what’s done so far and when you’ll return.',
  [RESCHEDULE_STEPS.OTHER_ACCESS_CALL]:
    'Call support for other access issues. If needed, reschedule the work order.',
  [RESCHEDULE_STEPS.OTHER_ACCESS_FORM]:
    'Describe the access problem you encountered and schedule your return.',
  [RESCHEDULE_STEPS.OTHER_ISSUE_FORM]:
    'Describe the issue you’re facing and select a new time to return.',
  [RESCHEDULE_STEPS.PARTS_NEEDED_LATER]: 'Pick a new day and time to complete the work.',
  [RESCHEDULE_STEPS.PARTS_NEEDED_TIME]: 'Tell us if you’re coming back today or need more time.',
  [RESCHEDULE_STEPS.PARTS_NEEDED_TODAY]: 'Select the time you plan to return today.',
  [RESCHEDULE_STEPS.NOT_HOME_CALL]:
    'Try calling the resident before rescheduling the work order. If they are unavailable, you can proceed with rescheduling.',
  [RESCHEDULE_STEPS.NOT_HOME_FORM]:
    'Upload a photo showing you were onsite and select a new time to return.',
  [RESCHEDULE_STEPS.WEATHER_FORM]: 'Provide a reason and schedule a new time with the resident.',
};

export const RESCHEDULE_FORM_IDS: Record<
  Extract<
    RescheduleStepsType,
    | 'lockbox-gate-form'
    | 'multi-day-form'
    | 'not-home-form'
    | 'other-access-form'
    | 'other-issue-form'
    | 'parts-needed-later'
    | 'parts-needed-today'
    | 'weather-form'
  >,
  string
> = {
  [RESCHEDULE_STEPS.LOCKBOX_GATE_FORM]: 'lockboxGateForm',
  [RESCHEDULE_STEPS.MULTI_DAY_FORM]: 'multiDayForm',
  [RESCHEDULE_STEPS.NOT_HOME_FORM]: 'notHomeForm',
  [RESCHEDULE_STEPS.OTHER_ACCESS_FORM]: 'otherAccessForm',
  [RESCHEDULE_STEPS.OTHER_ISSUE_FORM]: 'otherIssueForm',
  [RESCHEDULE_STEPS.PARTS_NEEDED_LATER]: 'partsNeededLaterForm',
  [RESCHEDULE_STEPS.PARTS_NEEDED_TODAY]: 'partsNeededTodayForm',
  [RESCHEDULE_STEPS.WEATHER_FORM]: 'weatherForm',
};

export const RESCHEDULE_REASON_VALUES = {
  MULTI_DAY: 'multi-day-job',
  OTHER_ISSUE: 'other-issue',
  PARTS_NEEDED: 'parts-needed',
  REPORT_ACCESS: 'report-access-issue',
  WEATHER: 'weather',
} as const;

export type RescheduleReasonKeyType =
  (typeof RESCHEDULE_REASON_VALUES)[keyof typeof RESCHEDULE_REASON_VALUES];

export type OptionWithStep<T> = OptionType<T> & {
  step: RescheduleStepsType;
};

export const RESCHEDULE_REASONS_LIST: OptionWithStep<RescheduleReasonKeyType>[] = [
  {
    label: 'Weather',
    value: RESCHEDULE_REASON_VALUES.WEATHER,
    step: RESCHEDULE_STEPS.WEATHER_FORM,
  },
  {
    label: 'Parts Needed',
    value: RESCHEDULE_REASON_VALUES.PARTS_NEEDED,
    step: RESCHEDULE_STEPS.PARTS_NEEDED_TIME,
  },
  {
    label: 'Multi-Day Job',
    value: RESCHEDULE_REASON_VALUES.MULTI_DAY,
    step: RESCHEDULE_STEPS.MULTI_DAY_FORM,
  },
  {
    label: 'Report Access Issue',
    value: RESCHEDULE_REASON_VALUES.REPORT_ACCESS,
    step: RESCHEDULE_STEPS.ACCESS_ISSUE_REASONS,
  },
  {
    label: 'Other Issue',
    value: RESCHEDULE_REASON_VALUES.OTHER_ISSUE,
    step: RESCHEDULE_STEPS.OTHER_ISSUE_FORM,
  },
];

export const SCHEDULE_OPTIONS = {
  ANOTHER_DAY: 'another-day',
  TODAY: 'today',
} as const;

export const ACCESS_ISSUE_KEYS = {
  LOCKBOX_GATE: 'lockbox-gate',
  NOT_HOME: 'not-home',
  OTHER: 'other',
} as const;

export type AccessIssueType = (typeof ACCESS_ISSUE_KEYS)[keyof typeof ACCESS_ISSUE_KEYS];
export type AccessIssueKeys = keyof typeof ACCESS_ISSUE_KEYS;

export const ACCESS_ISSUE_LABELS: Record<AccessIssueType, string> = {
  [ACCESS_ISSUE_KEYS.LOCKBOX_GATE]: 'Lockbox/Gate Issue',
  [ACCESS_ISSUE_KEYS.NOT_HOME]: 'Resident Not Home',
  [ACCESS_ISSUE_KEYS.OTHER]: 'Other Access Issue',
} as const;

export const ACCESS_ISSUE_OPTIONS: Record<AccessIssueKeys, OptionWithStep<AccessIssueType>> = {
  LOCKBOX_GATE: {
    label: ACCESS_ISSUE_LABELS[ACCESS_ISSUE_KEYS.LOCKBOX_GATE],
    value: ACCESS_ISSUE_KEYS.LOCKBOX_GATE,
    step: RESCHEDULE_STEPS.LOCKBOX_GATE_CALL,
  },
  NOT_HOME: {
    label: ACCESS_ISSUE_LABELS[ACCESS_ISSUE_KEYS.NOT_HOME],
    value: ACCESS_ISSUE_KEYS.NOT_HOME,
    step: RESCHEDULE_STEPS.NOT_HOME_CALL,
  },
  OTHER: {
    label: ACCESS_ISSUE_LABELS[ACCESS_ISSUE_KEYS.OTHER],
    value: ACCESS_ISSUE_KEYS.OTHER,
    step: RESCHEDULE_STEPS.OTHER_ACCESS_CALL,
  },
};

export const WO_DETAILS_PANEL_EVENT: string = 'openVendorWOSidePanel';

export const CTA_MAIN_ACTION_LABELS: TypedObj<string> = {
  en_route: 'Start Work',
  en_route_paused: 'Resume Travel',
  re_scheduled: 'Start En-route',
  scheduled: 'Start En-route',
  work_in_progress: 'Close Work Order',
  paused: 'Resume Work',
  schedule_return_trip: 'Schedule Return Trip',
  ready_to_schedule: 'Schedule Return Trip',
  in_progress: 'Close Work Order',
  in_progress_paused: 'Resume Work',
};

// In-context help for the main CTA, keyed on work-order status to mirror
// CTA_MAIN_ACTION_LABELS (COM-617). One action-oriented sentence each.
export const CTA_MAIN_ACTION_TOOLTIPS: TypedObj<string> = {
  en_route: "Stops travel and starts the job timer — you're now on the clock at the property.",
  en_route_paused: 'Restarts the travel timer where you paused it.',
  re_scheduled: "Starts the travel timer and notifies the resident you're on the way.",
  scheduled: "Starts the travel timer and notifies the resident you're on the way.",
  work_in_progress: "Finalizes this trip for review; you can't edit the report after closing.",
  paused: 'Restarts the job timer where you paused it.',
  schedule_return_trip: "Books a second visit that's tracked and paid as its own trip.",
  ready_to_schedule: "Books a second visit that's tracked and paid as its own trip.",
  in_progress: "Finalizes this trip for review; you can't edit the report after closing.",
  in_progress_paused: 'Restarts the job timer where you paused it.',
};

export const CTA_MAIN_ABILITIES: TypedObj<string> = {
  en_route: 'start_work',
  en_route_paused: 'resume_en_route',
  scheduled: 'start_en_route',
  re_scheduled: 'start_en_route',
  work_in_progress: 'close_work_order',
  paused: 'resume_work',
  schedule_return_trip: 'ready_to_schedule',
  ready_to_schedule: 'schedule',
  in_progress: 'close_work_order',
  in_progress_paused: 'resume_work',
};
// Cancellation of Work Order
export type CancellationWOReasonKeyType =
  (typeof CANCELLATION_REASON_KEYS)[keyof typeof CANCELLATION_REASON_KEYS];

export const CANCELLATION_REASON_KEYS = {
  CANT_COMPLETE: 'cant-complete',
  FIXED_ISSUE: 'already-fixed',
} as const;

export const CANCELLATION_REASONS: OptionType<CancellationWOReasonKeyType>[] = [
  {
    label: "Can't Complete Trip",
    value: CANCELLATION_REASON_KEYS.CANT_COMPLETE,
  },
  {
    label: 'Issues Already Resolved',
    value: CANCELLATION_REASON_KEYS.FIXED_ISSUE,
  },
];

export type CancellationWOReasonStepType = {
  key: CancellationWOReasonKeyType;
  step: {
    STEP_1: '1';
    STEP_2: '2';
    STEP_3: '3';
  };
};

export const CANCELLATION_WO_STEPS: CancellationWOReasonStepType['step'] = {
  STEP_1: '1',
  STEP_2: '2',
  STEP_3: '3',
} as const;

export const CANCELLATION_WO_DEFAULTS = {
  PAGE_TITLE: 'Cancel Trip',
  TITLE: 'Cancellation Reason',
  SUBTITLE: 'Select why you are canceling this trip.',
  ALERT_MESSAGE: 'Canceling within the arrival window may incur a no-show fee.',
} as const;

export const CANCELLATION_WO_DESCRIPTIONS = {
  CANT_COMPLETE: 'Explain why you are unable to complete the work and upload a photo if needed.',
  FIXED_ISSUE: 'Tell us how you confirmed the issues were already resolved.',
} as const;

export const CANCELLATION_WO_PLACEHOLDERS = {
  CANT_COMPLETE: 'Enter a reason, e.g., vehicle issue, scheduling conflict or equipment issue',
  FIXED_ISSUE: 'Explain how you found the issue resolved',
} as const;

export const CANCELLATION_WO_INPUT_LABELS = {
  CANT_COMPLETE: 'Reason',
  FIXED_ISSUE: 'Additional Details',
} as const;

export const PARTS_NEEDED_STEP_LABELS = {
  INITIAL_SELECTION: 'Parts Needed',
  TIME_SELECTION: 'Select Time',
  FILL_INFO: 'Additional Information',
  WO_PAUSED: 'Work Order Paused',
  RESCHEDULE_SUCCESS: 'Rescheduled',
};

const findSlot = (key: ScheduleSlotKeyType) =>
  SCHEDULE_SLOT_DEFINITIONS.find((s) => s.key === key)!;

/** Derived from SCHEDULE_SLOT_DEFINITIONS (single source of truth for slot hours/labels). */
export const PARTS_NEEDED_TIME_OPTIONS = {
  MORNING: {
    key: SCHEDULE_SLOT_KEYS.MORNING,
    label: findSlot(SCHEDULE_SLOT_KEYS.MORNING).label,
    time: findSlot(SCHEDULE_SLOT_KEYS.MORNING).timeLabel,
  },
  AFTERNOON: {
    key: SCHEDULE_SLOT_KEYS.AFTERNOON,
    label: findSlot(SCHEDULE_SLOT_KEYS.AFTERNOON).label,
    time: findSlot(SCHEDULE_SLOT_KEYS.AFTERNOON).timeLabel,
  },
  EVENING: {
    key: SCHEDULE_SLOT_KEYS.EVENING,
    label: findSlot(SCHEDULE_SLOT_KEYS.EVENING).label,
    time: findSlot(SCHEDULE_SLOT_KEYS.EVENING).timeLabel,
  },
} as const;

export type PartsNeededTimeOptionKeyType =
  (typeof PARTS_NEEDED_TIME_OPTIONS)[keyof typeof PARTS_NEEDED_TIME_OPTIONS]['key'];

export const RESCHEDULED_REASON_VALUES = {
  PARTS_NEEDED_TODAY: 'parts_needed_today',
  PARTS_NEEDED_ANOTHER_DAY: 'parts_needed_another_day',
} as const;

export const COMMON_RESCHEDULE_FORM_SCHEMA = {
  newScheduledTime: yup
    .object()
    .shape({
      startTime: yup.string().required(),
      endTime: yup.string().required(),
    })
    .required('Schedule is required'),
  confirmReturn: yup
    .boolean()
    .required('Must be confirmed with resident.')
    .oneOf([true], 'Must be confirmed with resident.'),
};

export const MEDIA_SHAPE = yup.object().shape({
  // File
  file: yup.mixed().optional(),
  thumbnailFile: yup.mixed().optional(),
  size: yup
    .number()
    .max(5 * 1024 * 1024, 'File size should not exceed 5MB')
    .optional(),
  mime_type: yup.string().optional(),
  file_name: yup.string().optional(),
  // Identifiers
  media_uuid: yup.string().optional(), // temporary media uuid from frontend
  stored_media_uuid: yup.string().optional(), // final media uuid from backend
  thumbnail_id: yup.string().optional(),
  // URL - for existing files
  original_uri: yup.string().optional(),
  thumbnail_uri: yup.string().optional(),
  // States
  isExisting: yup.boolean().optional(),
  isUploading: yup.boolean().optional(),
  isUploadFailed: yup.boolean().optional(),
});

export const PAUSE_NOTIFICATION = {
  PAUSE_TIMER:
    'Job timer paused. Resume it when you start working — forgetting to resume can undercount your billable time.',
  PAUSE_EN_ROUTE:
    'Travel timer is paused. Resume it when you start travelling again — forgetting to resume can undercount your billable time.',
} as const;
export const MISSING_INFO_NOTIFICATION = {
  MISSING_INFO: 'One or more issues need more info. Tap to update and resubmit',
} as const;
export const ISSUES_STATUS = {
  ASSIGNED: 'assigned',
  CANCELED: 'canceled',
  DONE: 'done',
  MISSING_INFO: 'missing_info',
  PENDING: 'pending',
  QUALITY_CHECK: 'quality_check',
  QUOTED: 'quoted',
  REASSIGNED: 'reassigned',
  UNRESOLVED: 'unresolved',
} as const;
// Why an issue reached the "unresolved" status (BE App\Enums\UnresolveType). Used to
// tell a trip-cancellation unresolve (hide the service report — ORI-144) apart from a
// vendor quality-check decline (show Edit reason / Mark as Pending — ORI-481).
export const UNRESOLVE_TYPE = {
  TECHNICIAN_DECLINE: 'technician_decline',
  CLOSE_WORKORDER: 'close_workorder',
  CANCEL_TRIP: 'cancel_trip',
  QUALITY_CHECK: 'quality_check',
} as const;
// Single source of truth for the `unresolved_from` value set (derived from the const
// above so the union can't drift from it).
export type UnresolveType = (typeof UNRESOLVE_TYPE)[keyof typeof UNRESOLVE_TYPE];
// Per-issue trip-pivot quality-check outcome (BE App\Enums\QualityCheckStatus: passed /
// missing_info / unresolved; `reassign` is resource-derived from an unresolved + new-WO
// issue). Only PASSED is load-bearing here — a QC-passed Done issue must render fully
// read-only in the vendor portal even when a sibling approved-quote reactivates the WO
// (ORI-588). The paired foresight-be#1609 adds the authoritative latest-trip-scoped
// guard on the destructive Mark-as-Pending path — the server-side backstop for this FE
// lock — once merged.
export const QUALITY_CHECK_STATUS = {
  PASSED: 'passed',
  MISSING_INFO: 'missing_info',
  UNRESOLVED: 'unresolved',
  REASSIGN: 'reassign',
} as const;
export type QualityCheckStatusType =
  (typeof QUALITY_CHECK_STATUS)[keyof typeof QUALITY_CHECK_STATUS];
export const ISSUES_STATUS_LABEL = {
  DONE: 'Done',
  MISSING_INFO: 'Missing Info',
  PENDING: 'Pending',
  QUOTED: 'Quoted',
  UNRESOLVED: 'Unresolved',
} as const;
export const ISSUES_STATUS_LABEL_MAP: Readonly<Record<string, string>> = {
  [ISSUES_STATUS.ASSIGNED]: 'Assigned',
  [ISSUES_STATUS.CANCELED]: 'Canceled',
  [ISSUES_STATUS.DONE]: ISSUES_STATUS_LABEL.DONE,
  [ISSUES_STATUS.MISSING_INFO]: ISSUES_STATUS_LABEL.MISSING_INFO,
  [ISSUES_STATUS.PENDING]: ISSUES_STATUS_LABEL.PENDING,
  [ISSUES_STATUS.QUALITY_CHECK]: 'Quality Check',
  [ISSUES_STATUS.QUOTED]: ISSUES_STATUS_LABEL.QUOTED,
  [ISSUES_STATUS.REASSIGNED]: 'Reassigned',
  [ISSUES_STATUS.UNRESOLVED]: ISSUES_STATUS_LABEL.UNRESOLVED,
  // The QC broadcast labels through this map, and `passed` is the one QC value with no
  // issue-status twin — without an entry it renders as the raw enum (COM-670).
  [QUALITY_CHECK_STATUS.PASSED]: 'Passed',
};
export const MEDIA_TYPE = {
  QUOTE_TASK_OPTION: 'quote-task-option',
  // ORI-715: shared constant for the vendor reschedule upload tag. `UploadMediaType`
  // collapses to `string`, so a hand-typed literal has no compile-time guard — routing
  // both reschedule forms through this const catches a typo at the one definition site.
  RESCHEDULE_MEDIA: 'reschedule-media',
} as const;
export const OPTION_DETAILS = [
  'Options let you submit different choices for a single quote task.',
  'For instance, if you have a clogged toilet, you could have one option to fix it and another to just replace the whole thing.',
];
export const CANCELLATION_REASONS_STEPS: CancellationWOReasonStepType[] = [
  {
    key: CANCELLATION_REASON_KEYS.CANT_COMPLETE,
    step: CANCELLATION_WO_STEPS,
  },
  {
    key: CANCELLATION_REASON_KEYS.FIXED_ISSUE,
    step: CANCELLATION_WO_STEPS,
  },
];

export const reportAccessIssueSchema = yup.object().shape({
  accessIssueReason: yup.string(),
  description: yup.string().when('accessIssueReason', {
    is: (val: string) => val === ACCESS_ISSUE_KEYS.OTHER,
    then: (schema) => schema.required('Description is required.'),
    otherwise: (schema) => schema.notRequired(),
  }),
  propertyPhoto: yup
    .array()
    .of(MEDIA_SHAPE)
    .when('accessIssueReason', {
      is: (val: string) => val === ACCESS_ISSUE_KEYS.NOT_HOME,
      then: (schema) => schema.min(1, 'Media is required').required('Media is required.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  newScheduledTime: yup.string().required('Schedule Time is required.'),
  confirmReturn: yup
    .boolean()
    .required('Must be confirmed with resident.')
    .oneOf([true], 'Must be confirmed with resident.'),
});

export type ReportAccessIssueFormState = yup.InferType<typeof reportAccessIssueSchema>;

export const QUOTE_STATUS_KEYS = {
  DRAFT: 'draft',
  PENDING_REVIEW: 'pending-review',
  PENDING_APPROVAL: 'pending-approval',
  PARTIALLY_APPROVED: 'partially-approved',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
} as const;

export const QUOTE_STATUS_LABELS = {
  PENDING_APPROVAL: 'Pending Approval',
} as const;

export const QUOTE_ALERT_DESCRIPTION = {
  DRAFT: 'You need to close work order to submit this quote.',
  PENDING_APPROVAL:
    "Waiting for quote review and approval — you can leave the site while you wait; you'll be notified when it's decided.",
  APPROVED_TITLE: 'Quote Fully Approved',
  APPROVED_DESCRIPTION: 'Please schedule a return trip.',
  PARTIALLY_APPROVED_TITLE: 'Quote Partially Approved',
  PARTIALLY_APPROVED_DESCRIPTION:
    'Schedule a return trip and start the approved work now; the rest still needs sign-off.',
  QUALITY_CHECK: 'Waiting for quality check by coordinator.',
  REJECTED: 'Your quote was rejected.',
  MISSING_INFO: 'One or more issues need more info. Tap to update and resubmit.',
} as const;
export type RescheduleStepsType = (typeof RESCHEDULE_STEPS)[keyof typeof RESCHEDULE_STEPS];
export type RescheduleReasonsType = (typeof RESCHEDULE_REASONS)[keyof typeof RESCHEDULE_REASONS];

export const GOOGLE_MAPS_LOCATION_URL = 'https://www.google.com/maps/search/?api=1&query=';

export const MATERIAL_QUANTITY_TYPES: TypedObj<OptionType> = {
  units: { label: 'Units', value: 'units' },
  'square-ft': { label: 'Square ft', value: 'square-ft' },
  'linear-ft': { label: 'Linear ft', value: 'linear-ft' },
  pounds: { label: 'Pounds', value: 'pounds' },
} as const;

export const CTA_OTHER_ACTION_LABELS: string[] = [
  WO_STATUS_KEYS.EN_ROUTE,
  WO_STATUS_KEYS.EN_ROUTE_PAUSED,
  WO_STATUS_KEYS.SCHEDULED,
  WO_STATUS_KEYS.IN_PROGRESS,
  WO_STATUS_KEYS.IN_PROGRESS_PAUSED,
];
